// VOY-401: Client helper for the Duffel Places Suggestions backend proxy
// (`GET /api/flights/places/suggest`). Returns AirportCode-compatible results
// so it can be a drop-in replacement for `searchAirportCodes` at user-typing
// call sites. On any upstream failure it silently falls back to the Supabase
// airport_codes lookup — the feature is additive, never a regression.
//
// Goes directly through `fetch` (not `apiFetch`) so upstream hiccups do not
// surface user-facing toasts; the fallback path is invisible by design.
import { supabase } from './supabase'
import { searchAirportCodes, type AirportCode } from './airport-codes'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

interface DuffelPlace {
  id: string
  type: 'airport' | 'city'
  name: string
  iata_code: string
  iata_city_code?: string
  iata_country_code?: string
  city_name?: string
  city?: {
    name?: string
    iata_country_code?: string
  }
}

// Duffel does not return a full country name, only `iata_country_code` (ISO-2).
// We show that code in the dropdown label so users can still disambiguate
// between same-named cities (e.g. San José, CR vs San José, US).
function pickCountryDisplay(place: DuffelPlace): string {
  return (
    place.iata_country_code ||
    place.city?.iata_country_code ||
    ''
  )
}

function pickCityDisplay(place: DuffelPlace): string {
  if (place.type === 'airport') {
    return place.city_name || place.city?.name || place.name
  }
  return place.name
}

function toAirportCode(place: DuffelPlace): AirportCode | null {
  if (!place?.iata_code) return null
  const city = pickCityDisplay(place)
  const country = pickCountryDisplay(place)
  if (!city) return null
  return {
    code: place.iata_code,
    city,
    country,
    // Duffel doesn't return continent; the field is present on AirportCode
    // but unused by the dropdown UI.
    continent: '',
  }
}

async function buildAuthHeader(): Promise<string | null> {
  try {
    const session = (await supabase.auth.getSession()).data.session
    if (session?.access_token) return `Bearer ${session.access_token}`
  } catch {
    // fall through to API key
  }
  const apiKey = import.meta.env.VITE_API_KEY
  return apiKey ? `Bearer ${apiKey}` : null
}

export async function searchPlaces(query: string): Promise<AirportCode[]> {
  if (!query || query.length < 2) return []

  try {
    const auth = await buildAuthHeader()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (auth) headers.Authorization = auth

    const res = await fetch(
      `${API_URL}/api/flights/places/suggest?query=${encodeURIComponent(query)}`,
      { headers, credentials: 'include' },
    )
    if (!res.ok) return searchAirportCodes(query)

    const json = await res.json()
    const raw: DuffelPlace[] = Array.isArray(json?.data) ? json.data : []
    const mapped = raw
      .map(toAirportCode)
      .filter((p): p is AirportCode => p !== null)

    if (mapped.length === 0) {
      return searchAirportCodes(query)
    }

    return mapped
  } catch (err) {
    console.warn('[searchPlaces] Duffel lookup failed, falling back:', err)
    return searchAirportCodes(query)
  }
}
